import { PHOTOGRAMMETRY_JOB_NAME } from "../../../constants";
import { formatInputValuesToArgs } from "./format";

/**
 * Builds an updated translatedPipeline by replacing each job's
 * `main.args` with the CLI args formatted from the current form values.
 * For jobs with `trainedModelFilters`, also rewrites `preExec.args[0]`
 * (a stringified JSON blob) to inject the chosen trained model version's
 * `weightsLocation` and `configLocation` into
 * `downloadModelWeightsFile.from` and `downloadModelConfigFile.from`.
 * For jobs with `anotherDateSelection`, injects each picked mission's
 * resolved `preExecItem` into `preExec.args[0]` under
 * `<inputKey>DownloadSelected`. Does not mutate the input
 * translatedPipeline.
 *
 * When `jobNamesToUpdate` is provided, only those jobs have their args
 * rebuilt from `formValues`; every other job keeps its original args
 * untouched. This is required for relaunch, where only the editable jobs
 * (photogrammetry and its downstream dependents) have form values — the
 * rest are disabled and therefore stripped from the submit payload by
 * react-hook-form, so rebuilding them would throw on missing values.
 *
 * @param {Object} translatedPipeline - the original translatedPipeline
 *     (expected shape: { spec: { jobs: Object[] } })
 * @param {Object<string, {
 *   main: Object<string, any>,
 *   preExec?: { trainedModelVersion: null | { weightsLocation: string, configLocation: string } }
 * }>} formValues - keyed by job name
 * @param {Object<string, Object>} jobNameToModuleVersion - map from job
 *     name to a ModuleVersion object (see MODEL_PROPTYPES.ModuleVersion)
 * @param {Set<string>} [jobNamesToUpdate] - if provided, restricts the
 *     args rebuild to these job names; defaults to every job.
 * @returns {Object} a new translatedPipeline with updated job args
 */
export function buildUpdatedTranslatedPipeline(
    translatedPipeline,
    formValues,
    jobNameToModuleVersion,
    jobNamesToUpdate
) {
    const result = structuredClone(translatedPipeline);

    result.spec.jobs.forEach((job) => {
        if (jobNamesToUpdate && !jobNamesToUpdate.has(job.name)) return;

        const moduleVersion = jobNameToModuleVersion[job.name];
        if (!moduleVersion)
            throw new Error(
                `Module version not found for job name: ${job.name}`
            );

        job.main.args = formatInputValuesToArgs(
            moduleVersion.definition.inputs,
            formValues[job.name].main
        );

        if (job.trainedModelFilters) {
            const version = formValues[job.name].preExec.trainedModelVersion;
            const blob = JSON.parse(job.preExec.args[0]);
            blob.downloadModelConfigFile.from = version.configLocation;
            blob.downloadModelWeightsFile.from = version.weightsLocation;
            job.preExec.args[0] = JSON.stringify(blob);
        }

        // For jobs with `anotherDateSelection`, inject the resolved
        // `preExecItem` of each picked mission into `preExec.args[0]` under
        // `<inputKey>DownloadSelected`. Inputs the user left unselected
        // (null in the form) have any stale entry removed, so a de-selected
        // sibling mission is not silently re-downloaded on relaunch. The
        // matching `main.<inputKey>` value (the `preExecItem.to` path, or
        // null) is already carried by the args rebuilt above.
        if (job.anotherDateSelection) {
            const selections =
                formValues[job.name].preExec?.anotherDateSelection ?? {};
            const blob = JSON.parse(job.preExec.args[0]);
            Object.keys(job.anotherDateSelection).forEach((inputKey) => {
                const resolvedPreExecItem = selections[inputKey];
                if (resolvedPreExecItem) {
                    blob[`${inputKey}DownloadSelected`] = resolvedPreExecItem;
                } else {
                    // Cleared (or never picked): drop any stale entry a
                    // previous run persisted, so relaunch does not keep
                    // downloading a de-selected sibling mission's result.
                    delete blob[`${inputKey}DownloadSelected`];
                }
            });
            job.preExec.args[0] = JSON.stringify(blob);
        }
    });

    return result;
}

/**
 * Returns the set of job names that a relaunch would restart: the
 * photogrammetry job and all of its transitive downstream dependents.
 *
 * Mirrors the backend's `findJobsToRelaunch` (BFS over a reverse
 * dependency map: parent -> children). Returns an empty set when no
 * photogrammetry job is present, so callers can treat "nothing editable"
 * and "relaunch unavailable" identically.
 *
 * @param {{ name: string, dependencies?: string[] }[]} jobs
 * @returns {Set<string>} job names to relaunch
 */
export function getRelaunchableJobNames(jobs) {
    const hasPhotogrammetry = jobs.some(
        (job) => job.name === PHOTOGRAMMETRY_JOB_NAME
    );
    if (!hasPhotogrammetry) return new Set();

    const childrenMap = new Map();
    jobs.forEach((job) => {
        (job.dependencies || []).forEach((dep) => {
            if (!childrenMap.has(dep)) childrenMap.set(dep, []);
            childrenMap.get(dep).push(job.name);
        });
    });

    const result = new Set([PHOTOGRAMMETRY_JOB_NAME]);
    const queue = [PHOTOGRAMMETRY_JOB_NAME];
    while (queue.length > 0) {
        const current = queue.shift();
        (childrenMap.get(current) || []).forEach((child) => {
            if (!result.has(child)) {
                result.add(child);
                queue.push(child);
            }
        });
    }

    return result;
}
